import type { DocumentVersion } from "@prismicio/editor-fields"
import { getDateDiffString } from "@prismicio/editor-support/Date"
import { overflow } from "@prismicio/editor-support/String"

import type { DocumentVersionStatus } from "../../../app/services/core"
import type { Releases } from "../../../app/services/release"
import type { DocumentDraft } from "../../useDraft"

export function getDateTimeString(
  timestamp?: Date,
  timeZone?: Intl.DateTimeFormatOptions["timeZone"],
) {
  if (!timestamp) return ""

  const dateString = timestamp?.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  })

  const time = timestamp
    ?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    })
    .replace(" ", "")

  return `${dateString} - ${time}`
}

// Old wroom has given no character limit to release labels, so to handle this
// we need to overflow the label
export function formatReleaseLabel(label?: string) {
  if (!label) return ""

  return overflow(label, 20)
}

interface PlannedRelease {
  title?: string
  date?: string
  subtitle: string
  version: DocumentVersion
  migration: boolean
}

export function getReleaseDisplayInfos(
  repositoryPlannedReleases: Releases | undefined,
  documentReleaseVersions: DocumentVersion[],
) {
  const plannedReleasesMap = new Map(
    repositoryPlannedReleases?.map((release) => [release.id, release]),
  )

  const releases = documentReleaseVersions.reduce(
    (acc: PlannedRelease[], version) => {
      if (version.status !== "release") return acc

      const documentRelease = plannedReleasesMap.get(version.release_id)
      if (!documentRelease) return acc

      const editedBy = subtitle(version.author)
      const migration = documentRelease.migration || false
      // Migration release should display version timestamp
      const date = getDateTimeString(
        migration ? version.last_modified_date : documentRelease.timestamp,
      )

      return [
        ...acc,
        {
          title: formatReleaseLabel(documentRelease.label),
          subtitle: editedBy ? `last edited by ${editedBy}` : "",
          version,
          date,
          migration,
        },
      ]
    },
    [],
  )

  return {
    plannedReleases: releases.filter((r) => !r.migration),
    migrationReleases: releases.filter((r) => r.migration),
  }
}

export function getLocalVersion(
  version: DocumentVersion | undefined,
  draft: DocumentDraft,
  hideLocalDraft: boolean,
) {
  // For Release Versions we save straight to the version and not to a draft
  if (version?.status === "release" || !draft.editedAt) return undefined

  const unsavedDraft = {
    id: "local",
    header: "Unsaved draft",
    title: title(draft.editedAt),
    selected: true,
    onSelect: () => {},
  } as const

  return !hideLocalDraft ? unsavedDraft : undefined
}

export function getVersionDisplayInfo(
  versions: DocumentVersion[],
  status: DocumentVersionStatus,
) {
  const version = versions.find((version) => version.status === status)
  if (!version) return undefined

  return {
    title: title(version.last_modified_date),
    subtitle: subtitle(version.author),
    version,
  }
}

// TODO: This whole thing is broken because it doesn't refresh live.
// Use a specialized relative time component.
function title(date: Date) {
  return getDateDiffString(date)
}

export function subtitle(author: DocumentVersion["author"]) {
  if (author.first_name && author.last_name)
    return `${author.first_name} ${author.last_name}`

  return author.email
}
